import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useContext,
} from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import FileUpload from "../../components/common/FileUpload";
import { httpService } from "../../services/httpService";
import { TokenContext } from "../../context/TokenContext";

const VetClinicDrawer = ({
  isOpen,
  serviceId,
  onClose,
  fromSetup = false, 
  onSaveComplete = () => {},
}) => {
  const [loadingD, setLoadingD] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedVetCategory, setSelectedVetCategory] = useState(null);
  const [selectedVetService, setSelectedVetService] = useState(null);
  const [availableVetServices, setAvailableVetServices] = useState([]);
  const [serviceName, setServiceName] = useState("");
  const [taxable, setTaxable] = useState("no");
  const [taxPercentage, setTaxPercentage] = useState("");
  const [vetCategories, setVetCategories] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const { decodedData } = useContext(TokenContext);
  const [vendorServices, setVendorServices] = useState([]);
  const [selectedVendorServiceId, setSelectedVendorServiceId] = useState(null);
  const [isLoadingVendorServices, setIsLoadingVendorServices] = useState(false);
  const vendorId = decodedData?.data?.vendor?._id;

  const serviceCategories = useMemo(() => {
    return (
      decodedData?.data?.vendor?.service_category.filter(
        (cat) =>
          cat.type_id === 1 && cat.service_category_name === "Vet Clinic",
      ) || []
    );
  }, [decodedData]);

  const [petTypes, setPetTypes] = useState([]);
  const [selectedPetTypes, setSelectedPetTypes] = useState([]);
  const [petDropdownOpen, setPetDropdownOpen] = useState(false);
  const [loadingPetTypes, setLoadingPetTypes] = useState(false);
  const petDropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        petDropdownRef.current &&
        !petDropdownRef.current.contains(event.target)
      ) {
        setPetDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch pet types on mount
  useEffect(() => {
    const fetchPetTypes = async () => {
      setLoadingPetTypes(true);
      try {
        const res = await httpService.get(
          "/vendors/getPetTypes?isEnabled=true",
        );
        const list = res.data?.data ?? res.data ?? [];
        setPetTypes(list);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load pet types");
      } finally {
        setLoadingPetTypes(false);
      }
    };

    fetchPetTypes();
  }, []);

  // Update form.petType whenever selectedPetTypes changes
  useEffect(() => {
    const ids = selectedPetTypes.map((pt) => pt.pet_type_id);
    markChange({ petType: ids });
  }, [selectedPetTypes]);

  const togglePetType = (pt) => {
    setSelectedPetTypes((prev) => {
      const exists = prev.some((p) => p.pet_type_id === pt.pet_type_id);
      return exists
        ? prev.filter((p) => p.pet_type_id !== pt.pet_type_id)
        : [...prev, pt];
    });
  };

  const handleSelectAllPetTypes = () => {
    if (selectedPetTypes.length === petTypes.length) {
      setSelectedPetTypes([]);
    } else {
      setSelectedPetTypes(petTypes);
    }
  };

  const removePetType = (id) => {
    setSelectedPetTypes((prev) => prev.filter((p) => p.pet_type_id !== id));
  };

  // Fetch vet categories when drawer opens
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await httpService.get("/vetservice/getVetServices");
        if (response.code === 200) {
          setVetCategories(response.data || []);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        toast.error("Failed to load categories");
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const genBarcode = useCallback(async () => {
    try {
      const response = await httpService.get(
        "vendorvetclinicservice/generatevendorvetclinicservicebarcode",
      );
      return response.data.barcode;
    } catch (error) {
      console.error("Failed to generate service barcode.", error);
      return null;
    }
  }, []);

  const markVetStepComplete = useCallback(async () => {
  if (!vendorId) {
    console.warn("No vendorId available, skipping vet step completion.");
    return;
  }
  try {
    await httpService.put(`/vendorregistration/updatesetup/${vendorId}`, {
      steps: [{ key: "service", completed: true }],
    });
    console.log(" Vet clinic step marked complete for vendor", vendorId);
  } catch (error) {
    console.error(" Failed to mark vet clinic step complete:", error);
  }
}, [vendorId]);

  const getDefaultServiceCategory = useCallback(() => {
    if (serviceCategories.length > 0) {
      const vetClinic = serviceCategories[0];
      const vetType =
        vetClinic.service_type?.find((st) =>
          st.service_type_name.toLowerCase().includes("vet clinic"),
        ) || vetClinic.service_type?.[0];

      if (vetType) {
        return {
          service_category_id: vetClinic.service_category_id,
          service_type_id: vetType.service_type_id,
          service_type_name: vetType.service_type_name,
          service_category_type_id: vetType.service_category_type_id,
          type_id: vetClinic.type_id,
          service_category_name: vetClinic.service_category_name,
        };
      }
    }
    return null;
  }, [serviceCategories]);

  const emptyService = useMemo(
    () => ({
      name: "",
      barcode: null,
      category: getDefaultServiceCategory(),
      petType: [],
      bookable_online: "no",
      flatDurationMinutes: "",
      basicPrice: "",
      description: "",
      images: [],
      vetCategory: null,
      vetService: null,
      addons: [],
    }),
    [getDefaultServiceCategory],
  );

  const [form, setForm] = useState(emptyService);

  // Reset everything when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setForm(emptyService);
      setTaxable("no");
      setTaxPercentage("");
      setHasUnsavedChanges(false);
      setShowConfirmDialog(false);
      setTouched({});
      setErrors({});
      setSelectedVetCategory(null);
      setSelectedVetService(null);
      setServiceName("");
      setSelectedAddons([]);
      setSelectedPetTypes([]);
    }
  }, [isOpen, emptyService]);

  // Load existing service when editing
  useEffect(() => {
    if (serviceId && isOpen && vetCategories.length > 0) {
      (async () => {
        setLoadingD(true);
        try {
          const serviceRes = await httpService.get(
            `/vendorvetclinicservice/getvendorvetclinicservice/${serviceId}`,
          );
          const serviceData = serviceRes.data || {};

          const isTaxable =
            serviceData.tax_percentage && serviceData.tax_percentage > 0
              ? "yes"
              : "no";
          const taxPercent =
            serviceData.tax_percentage && serviceData.tax_percentage > 0
              ? serviceData.tax_percentage.toString()
              : "";

          const durationMinutes =
            (serviceData.duration?.flat?.hour || 0) * 60 +
            (serviceData.duration?.flat?.minutes || 0);

          const serviceAddons = serviceData.addons || [];
          setSelectedAddons(serviceAddons.map((addon) => addon.adon_id));

          setForm({
            name: serviceData.name || "",
            barcode: serviceData.barcode || null,
            category:
              serviceData.service_category || getDefaultServiceCategory(),
            petType: serviceData.pet_type || [],
            bookable_online: serviceData.bookable_online || "no",
            flatDurationMinutes: durationMinutes || "",
            basicPrice: serviceData.basic_price ?? "",
            description: serviceData.description || "",
            images: serviceData.images || [],
            vetCategory: null,
            vetService: null,
            addons: serviceAddons,
          });

          setServiceName(serviceData.name || "");
          setTaxable(isTaxable);
          setTaxPercentage(taxPercent);

          // Resolve vet category & service from stored ids or name
          let foundCategory = null;
          let foundService = null;

          if (serviceData.vet_category_id) {
            foundCategory = vetCategories.find(
              (c) => String(c._id) === String(serviceData.vet_category_id),
            );
          }
          if (foundCategory && serviceData.vet_service_id) {
            foundService = foundCategory.vet_services?.find(
              (s) => String(s._id) === String(serviceData.vet_service_id),
            );
          }
          if (!foundService && serviceData.name) {
            for (const category of vetCategories) {
              foundService = category.vet_services?.find(
                (s) => s.name === serviceData.name,
              );
              if (foundService) {
                foundCategory = category;
                break;
              }
            }
          }

          const catalogDescription =
            (serviceData.description &&
              String(serviceData.description).trim()) ||
            foundService?.description ||
            "";

          if (foundCategory && foundService) {
            setSelectedVetCategory(foundCategory._id);
            setAvailableVetServices(foundCategory.vet_services || []);
            setSelectedVetService(foundService._id);
            setForm((prev) => ({
              ...prev,
              vetCategory: foundCategory._id,
              vetService: foundService._id,
              description: catalogDescription,
            }));
          } else {
            if (catalogDescription) {
              setForm((prev) => ({
                ...prev,
                description: catalogDescription,
              }));
            }
            if (serviceData.vet_category_name || serviceData.name) {
              setServiceName(serviceData.name || "");
            }
          }

          // Restore selected pet types
          if (serviceData.pet_type && Array.isArray(serviceData.pet_type)) {
            const petTypeIds = serviceData.pet_type.map((p) =>
              typeof p === "object" ? p.id || p._id || p.pet_type_id : p,
            );
            const matched = petTypes.filter((pt) =>
              petTypeIds.some((id) => String(id) === String(pt.pet_type_id)),
            );
            setSelectedPetTypes(matched);
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to load service");
        } finally {
          setLoadingD(false);
        }
      })();
    }
  }, [
    serviceId,
    isOpen,
    vetCategories,
    petTypes,
    getDefaultServiceCategory,
    genBarcode,
  ]);

  // Update available services when category changes
  useEffect(() => {
    if (selectedVetCategory) {
      const category = vetCategories.find(
        (cat) => cat._id === selectedVetCategory,
      );
      setAvailableVetServices(category?.vet_services || []);
    } else {
      setAvailableVetServices([]);
    }
  }, [selectedVetCategory, vetCategories]);

  // Update service name when vet service selected
  useEffect(() => {
    if (selectedVetService) {
      const service = availableVetServices.find(
        (s) => s._id === selectedVetService,
      );
      if (service) {
        setServiceName(service.name);
        markChange({ name: service.name });
      }
    } else {
      setServiceName("");
      markChange({ name: "" });
    }
  }, [selectedVetService, availableVetServices]);

  const handleVetCategoryChange = (categoryId) => {
    setSelectedVetCategory(categoryId);
    setSelectedVetService(null);
    markChange({ vetCategory: categoryId, vetService: null });
  };

  const handleVetServiceChange = (serviceId) => {
    setSelectedVetService(serviceId);
    markChange({ vetService: serviceId });
  };

  const markChange = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setHasUnsavedChanges(true);
  };

  const validate = useCallback(
    (field, value) => {
      const newErrors = { ...errors };
      // Individual field validation logic (same as before)
      // Simplified here for brevity – kept in validateAll below
      setErrors(newErrors);
    },
    [errors],
  );

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleTaxableChange = (value) => {
    setTaxable(value);
    setHasUnsavedChanges(true);
    if (value === "no") {
      setTaxPercentage("");
    }
  };

  const handleTaxPercentageChange = (value) => {
    setTaxPercentage(value);
    setHasUnsavedChanges(true);
  };

  const validateAll = () => {
    const errs = {};
    if (!serviceName.trim()) errs.name = "Please select a service name";
    if (selectedPetTypes.length === 0) errs.petType = "Pet type is required";
    if (!selectedVetCategory) errs.vetCategory = "Vet category required";
    if (!selectedVetService) errs.vetService = "Vet service required";
    if (!form.basicPrice || Number(form.basicPrice) <= 0)
      errs.basicPrice = "Valid basic price required";
    if (taxable === "yes" && (!taxPercentage || Number(taxPercentage) <= 0))
      errs.taxPercentage = "Please enter a valid Service Charge";
    if (!form.flatDurationMinutes || Number(form.flatDurationMinutes) <= 0)
      errs.flatDurationMinutes = "Duration must be > 0 minutes";
    return errs;
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setShowConfirmDialog(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleSubmit = async () => {
    const errs = validateAll();
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs).join("\n"));
      return;
    }

    setIsSaving(true);

    const payload = {
      name: serviceName,
      description: form.description,
      barcode: form.barcode,
      service_category: null,
      pet_type: selectedPetTypes.map((pt) => pt.pet_type_id),
      duration: {
        duration_based_on: "flat",
        flat: {
          hour: 0,
          minutes: Number(form.flatDurationMinutes),
        },
        small: { hour: 0, minutes: 0 },
        medium: { hour: 0, minutes: 0 },
        large: { hour: 0, minutes: 0 },
        giant: { hour: 0, minutes: 0 },
      },
      bookable_online: form.bookable_online,
      service_inclusions: [],
      images: form.images,
      basic_price: Number(form.basicPrice),
      taxable: taxable,
      tax_percentage: taxable === "yes" ? Number(taxPercentage) : 0,
      vet_category_id: selectedVetCategory,
      vet_service_id: selectedVetService,
    };

    if (!serviceId) {
      toast.error("Select a service from the list to configure.");
      setIsSaving(false);
      return;
    }

    try {
      await httpService.put(
        `/vendorvetclinicservice/updatevendorvetclinicservice/${serviceId}`,
        { ...payload, is_configured: true, status: "active" },
      );
      toast.success("Service saved and activated successfully");
        await markVetStepComplete();
       if (onSaveComplete) {
        
         onSaveComplete(); 
       }
      setHasUnsavedChanges(false);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to save service");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Confirm Close Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Unsaved Changes</h3>
            <p className="mb-6">
              You have unsaved changes. Are you sure you want to close?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmClose}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Close anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Drawer */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-40"
          onClick={handleClose}
        />
        <div
          className={`absolute right-0 top-0 h-full w-full lg:w-3/4 xl:w-1/2 bg-white shadow-xl transform transition-transform duration-300 ease-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center p-4 border-b">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleClose}
                  className="p-1 rounded-full hover:bg-gray-100"
                  aria-label="Go back"
                >
                  <ArrowLeftIcon className="w-6 h-6" />
                </button>
                <h2 className="text-2xl font-semibold">
                  Configure Vet Clinic Service
                </h2>
              </div>
            </div>

            {loadingD && (
              <div className="absolute inset-0 z-20 bg-white bg-opacity-70 flex items-center justify-center">
                <div className="text-lg">Loading service data...</div>
              </div>
            )}

            <div className="flex-1 overflow-auto p-6 pb-28">
              <div className="space-y-6">
                {/* Vet Appointment & Service Name */}
                <div className="grid grid-cols-1 gap-4">
                  {/* <div>
                    <label className="block mb-1">
                      Vet Appointment <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={
                        vetCategories.find(
                          (c) => String(c._id) === String(selectedVetCategory)
                        )?.vet_category ||
                        serviceName ||
                        ""
                      }
                      className="w-full p-2 border rounded border-gray-300 bg-gray-50"
                    />
                  </div> */}

                  <div>
                    <label className="block mb-1">
                      Service Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={serviceName}
                      className="w-full p-2 border rounded border-gray-300 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Pet Type, Bookable, Duration */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="relative" ref={petDropdownRef}>
                    <label className="block mb-1">
                      Pet Type <span className="text-red-500">*</span>
                    </label>
                    <div
                      className="w-full px-3 py-2 border rounded bg-white min-h-[42px] flex flex-wrap items-center gap-2 cursor-pointer"
                      onClick={() => setPetDropdownOpen((prev) => !prev)}
                    >
                      {selectedPetTypes.length === 0 ? (
                        <span className="text-gray-500">
                          {loadingPetTypes ? "Loading..." : "Select Pet Types"}
                        </span>
                      ) : (
                        selectedPetTypes.map((pt) => (
                          <span
                            key={pt.pet_type_id}
                            className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full"
                          >
                            {pt.name}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePetType(pt.pet_type_id);
                              }}
                              className="ml-1 hover:bg-green-200 rounded-full w-5 h-5 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </span>
                        ))
                      )}
                      <svg
                        className={`ml-auto w-5 h-5 transition-transform ${
                          petDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {petDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        <div
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                          onClick={handleSelectAllPetTypes}
                        >
                          <div className="w-5 h-5 rounded border flex items-center justify-center">
                            {selectedPetTypes.length === petTypes.length && "✓"}
                          </div>
                          <span>Select All</span>
                        </div>
                        {petTypes.map((pt) => {
                          const isSelected = selectedPetTypes.some(
                            (s) => s.pet_type_id === pt.pet_type_id,
                          );
                          return (
                            <div
                              key={pt.pet_type_id}
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                              onClick={() => togglePetType(pt)}
                            >
                              <div className="w-5 h-5 rounded border flex items-center justify-center">
                                {isSelected && "✓"}
                              </div>
                              <span>{pt.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block mb-1">Bookable Online</label>
                    <select
                      value={form.bookable_online}
                      onChange={(e) =>
                        markChange({ bookable_online: e.target.value })
                      }
                      className="w-full p-2 border rounded border-gray-300"
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1">
                      Duration (minutes) <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.flatDurationMinutes}
                      onChange={(e) =>
                        markChange({
                          flatDurationMinutes: Number(e.target.value),
                        })
                      }
                      className="w-full p-2 border rounded border-gray-300"
                    >
                      <option value="">Select duration</option>
                      {Array.from({ length: 20 }, (_, i) => (i + 1) * 15).map(
                        (m) => (
                          <option key={m} value={m}>
                            {m} minutes
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1">
                      Base Price <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-600">
                        ₹
                      </span>
                      <input
                        type="text"
                        min="0"
                        value={form.basicPrice}
                        onChange={(e) =>
                          markChange({ basicPrice: e.target.value })
                        }
                        className="w-full p-2 pl-8 border rounded border-gray-300"
                        placeholder="Enter base price"
                      />
                    </div>
                  </div>
                </div>

                {/* Taxable Service Charges */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1">
                      Do you want to add separate service charges?
                    </label>
                    <div className="flex gap-6">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="yes"
                          checked={taxable === "yes"}
                          onChange={() => handleTaxableChange("yes")}
                          className="form-radio"
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="no"
                          checked={taxable === "no"}
                          onChange={() => handleTaxableChange("no")}
                          className="form-radio"
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>

                  {taxable === "yes" && (
                    <div>
                      <label className="block mb-1">
                        Service Charges (%){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={taxPercentage}
                        onChange={(e) =>
                          handleTaxPercentageChange(e.target.value)
                        }
                        className="w-full p-2 border rounded border-gray-300"
                        placeholder="e.g. 5 or 18"
                      />
                    </div>
                  )}
                </div>

                {/* Description & Images */}
                <div>
                  <h3 className="text-lg font-medium mb-3">
                    Additional Information
                  </h3>
                  <label className="block mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      markChange({ description: e.target.value })
                    }
                    className="w-full p-2 border rounded min-h-[120px] border-gray-300"
                    placeholder="Enter a detailed description..."
                  />

                  <div className="mt-4">
                    <label className="block text-md font-medium mb-2">
                      Images
                    </label>
                    <FileUpload
                      uploadEndpoint="/upload/fileUpload"
                      acceptedTypes={["image/png", "image/jpeg", "image/gif"]}
                      multiple={true}
                      maxFiles={10}
                      moduleName="vendors"
                      docType="service"
                      existingUrls={form.images || []}
                      onUploadComplete={(urls) => {
                        setForm((prev) => ({ ...prev, images: urls }));
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="mt-auto p-4 border-t flex justify-end gap-3 bg-white">
              <button
                onClick={handleClose}
                className="px-6 py-2 border rounded hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-6 py-2 bg-green-600 text-white rounded disabled:opacity-60 hover:bg-green-700"
                type="button"
              >
                {isSaving
                  ? "Saving..."
                  : serviceId
                    ? "Update Service"
                    : "Save Service"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VetClinicDrawer;
